import { GUID, SceneLoader } from '@babylonjs/core';
import { GLTFFileLoader, IGLTFLoaderData } from '@babylonjs/loaders/glTF';
import { IGLTF, IMesh } from '@babylonjs/loaders/glTF/2.0/glTFLoaderInterfaces';
import { BabylonjsModule, BabylonjsModuleOptions } from '@modules/babylonjs';
import { Injectable, Zed } from '@modules/core';
import { World } from 'miniplex';

import { Entity } from '../../ecs';
import { onFirstModelLoaded, onModelLoadProgress } from '../../observable';

import { ZEDExtensionLoaderBase } from './ZEDExtensionLoaderBase';

export class ZEDExtensionLoaderManager implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('ZEDExtensionLoaderManager');
  private readonly ZED_EXTENSION = 'ZED_EXTENSION';

  private world: World;
  private options: BabylonjsModuleOptions;

  private loaders: ZEDExtensionLoaderBase<any>[] = [];
  public get registedLoaderCount() {
    return this.loaders.length;
  }
  private totalCount: number = 0;
  private loadedCount: number = 0;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
  }: {
    [Zed.WORLD_TOKEN]: World;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
  }) {
    this.world = world;
    this.options = babylonjs.options;

    this.resetCount();
  }
  public dispose() {
    this.loaders.forEach((l) => l.dispose());
    this.loaders = [];
  }
  private resetCount() {
    this.totalCount = 0;
    this.loadedCount = 0;
  }

  public registExtensionLoader<T extends Entity>(loader: ZEDExtensionLoaderBase<T>) {
    const loaderName = loader.getLoaderName();
    if (this.loaders.find((l) => l.getLoaderName() === loaderName)) {
      console.log(`${loaderName}은 이미 등록된 ExtensionLoader 입니다.`);
    } else {
      console.log(`registExtensionLoader : ${loaderName}`);

      this.loaders.push(loader);
    }
  }

  public unregistExtensionLoader<T extends Entity>(loader: ZEDExtensionLoaderBase<T>) {
    const loaderName = loader.getLoaderName();
    const index = this.loaders.findIndex((l) => l.getLoaderName() === loaderName);
    if (index !== -1) {
      this.loaders.splice(index, 1);
    }
  }

  public getInstanceLoader(extensionData?: any) {
    let complete = false;
    let entities: any[] = [];
    SceneLoader.OnPluginActivatedObservable.addOnce((loader) => {
      const isGLTFLoader = loader instanceof GLTFFileLoader;
      if (isGLTFLoader) {
        const gltfLoader = loader as unknown as GLTFFileLoader;

        let enable = true;
        let loaderData: IGLTFLoaderData | undefined;
        let gltfData: IGLTF | undefined;
        gltfLoader.onParsed = (data) => {
          enable = this.isZEDGltf(data);
          if (enable) {
            loaderData = data;
            gltfData = loaderData.json as IGLTF;
            const meshes = gltfData.meshes;
            if (meshes) {
              for (const mesh of meshes) {
                this.totalCount += mesh.primitives.length;
              }
            }
          }
        };

        gltfLoader.onMeshLoaded = (mesh) => {
          if (enable) {
            if (mesh.material) {
              this.onProgress();
            }
          }
        };

        gltfLoader.onMaterialLoaded = (material) => {
          if (enable) {
            this.loaders.forEach((l) => l.initializeMaterial(material));
          }
        };

        gltfLoader.onComplete = () => {
          if (enable) {
            if (gltfData) {
              const resultEntities = this.loaders.flatMap((l) => {
                if (gltfData) return l.initializeComponents(gltfData, extensionData);
                return [];
              });

              resultEntities.forEach((reultEntity) => {
                const index = entities.findIndex((entity) => entity.entityId === reultEntity.entityId);
                if (index !== -1) {
                  entities[index] = { ...entities[index], ...reultEntity };
                } else {
                  entities.push(reultEntity);
                }
              });

              entities.map((e) => this.world.add(e));
            }
          }

          complete = true;
        };
      }
    });

    return {
      entities: entities,
      onComplete: new Promise<void>((resolve) => {
        const checkCompletion = () => {
          if (complete) {
            resolve();
            this.onComplete();
          } else {
            setTimeout(checkCompletion, 50);
          }
        };
        checkCompletion();
      }),
    };
  }

  private deepMerge(target: any, source: any) {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (target[key] && typeof target[key] === 'object' && typeof source[key] === 'object') {
          if (!this.isClassInstance(target[key]) && !this.isClassInstance(source[key]))
            this.deepMerge(target[key], source[key]);
          else
            console.error(
              `순환참조 방지를 위해 class로 정의한 api는 다른 모듈의 api와 합칠 수 없습니다. 모듈의 [${key}] 관련 api를 확인하세요.`
            );
        } else {
          target[key] = source[key];
        }
      }
    }
  }

  private isClassInstance(obj: any): boolean {
    if (obj === null || obj === undefined) {
      return false;
    }

    // 객체의 생성자가 Object가 아니고, 프로토타입이 존재하면 클래스의 인스턴스로 간주
    // 이것은 클래스의 인스턴스가 일반적으로 생성자의 프로토타입을 상속받기 때문
    const proto = Object.getPrototypeOf(obj);
    return proto !== null && proto.constructor !== Object;
  }

  private isZEDGltf(data: IGLTFLoaderData) {
    // @ts-ignore
    const extensionsUsed: string[] = data.json['extensionsUsed'];
    return extensionsUsed?.includes(this.ZED_EXTENSION);
  }
  private onProgress() {
    // * Model Load 완료 시 Progress 콜백
    if (onModelLoadProgress.hasObservers()) {
      const progress = ++this.loadedCount / this.totalCount;
      onModelLoadProgress.notifyObservers(progress);
    }
  }
  private onComplete() {
    // * 첫 Model Load 완료 시 콜백
    if (this.loadedCount === 0) {
      if (onFirstModelLoaded.hasObservers()) {
        onFirstModelLoaded.notifyObservers(undefined);
      }
    }
  }
}
